// Isolated calculation logic (frontend mirror of backend/calculations.py).
// Keep all quoting formulas here so they can be updated in one place.

export const DIM_TO_METERS = { mm: 0.001, cm: 0.01, m: 1 };

export const CALC_UNITS = {
  m3: { key: "m3", label: "m\u00b3", long: "Cubic meter (m\u00b3)" },
  m2: { key: "m2", label: "m\u00b2", long: "Square meter (m\u00b2)" },
  linear_m: { key: "linear_m", label: "lin.m", long: "Linear meter" },
};

export const MATERIAL_TYPES = {
  glulam_beam_column: "Glulam beam / column",
  clt_panel: "CLT panel",
  timber_frame_member: "Timber-frame member",
  other: "Other",
};

export const PROJECT_CATEGORIES = {
  wooden_house: "Ahşap ev projeleri",
  structural: "Yapısal ahşap işleri",
};

export function toMeters(value, unit) {
  const n = parseFloat(value);
  if (!isFinite(n)) return 0;
  return n * (DIM_TO_METERS[unit] ?? 1);
}

export function computeMetrics(line) {
  const w = toMeters(line.width, line.width_unit || "mm");
  const t = toMeters(line.thickness, line.thickness_unit || "mm");
  const l = toMeters(line.length, line.length_unit || "m");
  const qty = parseFloat(line.quantity);
  const q = isFinite(qty) ? qty : 0;
  return {
    volume_m3: w * t * l * q,
    area_m2: w * l * q,
    linear_m: l * q,
    paint_m2: 2 * (w + t) * l * q,
  };
}

export function billableQuantity(calcUnit, metrics) {
  if (calcUnit === "m3") return metrics.volume_m3;
  if (calcUnit === "m2") return metrics.area_m2;
  if (calcUnit === "linear_m") return metrics.linear_m;
  return 0;
}

export function effectiveUnitPrice(line, material, priceMode) {
  const override = line.price_override;
  if (override !== null && override !== undefined && override !== "") {
    const v = parseFloat(override);
    if (isFinite(v)) return v;
  }
  if (priceMode === "locked") {
    const f = line.frozen_price;
    return f !== null && f !== undefined ? parseFloat(f) || 0 : 0;
  }
  return material ? parseFloat(material.unit_price) || 0 : 0;
}

export function computeLine(line, material, priceMode) {
  const metrics = computeMetrics(line);
  const calcUnit = material ? material.calc_unit : null;
  const unitPrice = effectiveUnitPrice(line, material, priceMode);
  const qtyBilled = billableQuantity(calcUnit, metrics);
  return {
    ...metrics,
    calcUnit,
    billableQuantity: qtyBilled,
    unitPrice,
    lineTotal: qtyBilled * unitPrice,
  };
}

export function grandTotal(lines, materialsById, priceMode) {
  return lines.reduce((sum, line) => {
    const mat = materialsById[line.material_id];
    return sum + computeLine(line, mat, priceMode).lineTotal;
  }, 0);
}

const nf = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function fmt(n) {
  if (!isFinite(n)) return "0,00";
  return nf.format(n);
}

export function fmtMoney(n) {
  return `${fmt(n)} \u20ac`;
}

// ---------- Wooden house (ahşap ev) ----------
export const WOOD_SYSTEM_TYPES = { clt: "CLT", yigma: "Yığma", karkas: "Karkas" };

export const DEFAULT_WH = {
  floor_m2: 0, roof_m2: 0, veranda_m2: 0, balcony_m2: 0,
  interior_walls: [], exterior_walls: [],
  interior_openings_m2: 0, exterior_openings_m2: 0,
  interior_coeff: 1, exterior_coeff: 1, quote_lines: [],
};

export function whWallArea(walls) {
  return (walls || []).reduce((s, w) => {
    const a = (parseFloat(w.width) || 0) * (parseFloat(w.height) || 0);
    return s + (isFinite(a) ? a : 0);
  }, 0);
}

export function whMetrics(wh) {
  const w = wh || {};
  const iw = whWallArea(w.interior_walls);
  const ew = whWallArea(w.exterior_walls);
  const interior = iw * (parseFloat(w.interior_coeff) || 1) - (parseFloat(w.interior_openings_m2) || 0);
  const exterior = ew * (parseFloat(w.exterior_coeff) || 1) - (parseFloat(w.exterior_openings_m2) || 0);
  return {
    interiorWallArea: iw,
    exteriorWallArea: ew,
    interiorCladding: Math.max(interior, 0),
    exteriorCladding: Math.max(exterior, 0),
  };
}

export function whLineTotal(line) {
  return (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
}

export function whGrandTotal(lines) {
  return (lines || []).reduce((s, l) => s + whLineTotal(l), 0);
}

export function buildStandardWHLines(wh, m) {
  const rows = [
    ["Zemin döşeme kaplaması", wh.floor_m2],
    ["Çatı kaplaması", wh.roof_m2],
    ["Dış duvar kaplaması (dış cephe)", m.exteriorCladding],
    ["İç duvar kaplaması (iç cephe)", m.interiorCladding],
    ["Veranda döşemesi", wh.veranda_m2],
    ["Balkon döşemesi", wh.balcony_m2],
  ];
  return rows.map(([description, quantity]) => ({
    id: crypto.randomUUID(),
    description,
    quantity: Number(quantity) || 0,
    unit: "m²",
    unit_price: 0,
  }));
}

// ---------- Frame house (Karkas / Yığma) ----------
export const DEFAULT_AREA = { width: 0, length: 0, area_override: 0 };
export const DEFAULT_FRAME = {
  footprint: { ...DEFAULT_AREA }, mezz1: { ...DEFAULT_AREA }, mezz2: { ...DEFAULT_AREA },
  roof_projection: { ...DEFAULT_AREA }, roof_slope_pct: 0,
  veranda: { ...DEFAULT_AREA }, balcony: { ...DEFAULT_AREA },
  windows: [], exterior_doors: [], interior_doors: [],
  interior_walls: [], exterior_walls: [],
  roof_structural_coeff: 0.04, floor_structural_coeff: 0.035, wall_coeff: 1,
  interior_wall_thickness: 0, exterior_wall_thickness: 0,
  roof_clt_thickness: 0, floor_clt_thickness: 0,
  overhead_pct: 0,
  components: [],
};

const areaVal = (a) => {
  const o = parseFloat(a?.area_override) || 0;
  return o > 0 ? o : (parseFloat(a?.width) || 0) * (parseFloat(a?.length) || 0);
};
const wallAreaSum = (arr) => (arr || []).reduce((s, w) => s + (parseFloat(w.width) || 0) * (parseFloat(w.height) || 0), 0);
const openAreaSum = (arr) => (arr || []).reduce((s, o) => s + (parseFloat(o.width) || 0) * (parseFloat(o.height) || 0) * (parseFloat(o.count) || 0), 0);

export function frameAreas(k) {
  const footprint = areaVal(k.footprint);
  const mezz = areaVal(k.mezz1) + areaVal(k.mezz2);
  const total = footprint + mezz;
  const slope = parseFloat(k.roof_slope_pct) || 0;
  const roof = areaVal(k.roof_projection) * Math.sqrt(1 + Math.pow(slope / 100, 2));
  return {
    footprint, mezz, total, roof,
    iw: wallAreaSum(k.interior_walls), ew: wallAreaSum(k.exterior_walls),
    win: openAreaSum(k.windows), extd: openAreaSum(k.exterior_doors), intd: openAreaSum(k.interior_doors),
    veranda: areaVal(k.veranda), balcony: areaVal(k.balcony),
  };
}

function yigmaWallVolume(k, a) {
  const it = parseFloat(k.interior_wall_thickness) || 0;
  const et = parseFloat(k.exterior_wall_thickness) || 0;
  const extGross = (k.exterior_walls || []).reduce((s, w) => s + ((parseFloat(w.width) || 0) + 0.5) * (parseFloat(w.height) || 0), 0) * et;
  const intGross = (k.interior_walls || []).reduce((s, w) => s + ((parseFloat(w.width) || 0) + 0.5) * (parseFloat(w.height) || 0), 0) * it;
  return Math.max((extGross - (a.win + a.extd) * et) + (intGross - a.intd * it), 0);
}

export function frameComponents(k, systemType) {
  const a = frameAreas(k);
  const wc = parseFloat(k.wall_coeff) || 1;
  const rc = parseFloat(k.roof_structural_coeff) || 0.04;
  const fc = parseFloat(k.floor_structural_coeff) || 0.035;
  const cl = (x) => Math.max(x, 0);
  const roofRows = [
    ["roof_lambri", "Çatı kaplama lambri", "m²", a.roof],
    ["roof_membrane", "Çatı membran izolasyon", "m²", a.roof],
    ["roof_wool", "Çatı taşyünü izolasyon", "m²", a.roof],
    ["roof_structural", "Çatı yapısal lamine", "m³", a.roof * rc],
  ];
  const floorRows = [
    ["mezz_insulation", "Arakat izolasyon (taşyünü/OSB/betopan)", "m²", a.mezz],
    ["floor_parquet", "Zemin parke", "m²", a.total],
    ["under_ceiling", "Alt kat ters tavan kaplaması", "m²", a.mezz],
    ["floor_structural", "Zemin yapısal lamine ahşap", "m³", a.total * fc],
  ];
  const deckRows = [
    ["veranda_deck", "Veranda döşeme", "m²", a.veranda],
    ["balcony_deck", "Balkon döşeme", "m²", a.balcony],
  ];
  let rows;
  if (systemType === "yigma") {
    rows = [["structural_wall", "Yığma yapısal duvar", "m³", yigmaWallVolume(k, a)], ...roofRows, ...floorRows, ...deckRows];
  } else if (systemType === "clt") {
    const it = parseFloat(k.interior_wall_thickness) || 0, et = parseFloat(k.exterior_wall_thickness) || 0;
    const rct = parseFloat(k.roof_clt_thickness) || 0, fct = parseFloat(k.floor_clt_thickness) || 0;
    rows = [
      ["clt_wall_m2", "CLT yapısal duvar (alan)", "m²", a.iw + a.ew],
      ["clt_wall_m3", "CLT yapısal duvar (hacim)", "m³", a.iw * it + a.ew * et],
      ...roofRows,
      ["roof_clt_panel", "Çatı CLT panel", "m³", a.roof * rct],
      ...floorRows,
      ["floor_clt_panel", "Döşeme CLT panel", "m³", a.mezz * fct],
      ["ext_facade", "Dış cephe kaplama", "m²", cl(a.ew - (a.win + a.extd))],
      ["int_facade", "İç cephe kaplama", "m²", cl(((a.iw - a.intd) * 2 + a.ew) - (a.win + a.extd))],
      ...deckRows,
    ];
  } else {
    rows = [
      ["structural_wall", "Karkas yapısal duvar", "m²", cl(((a.iw + a.ew) - (a.win + a.extd + a.intd)) * wc)],
      ...roofRows, ...floorRows,
      ["ext_facade", "Dış cephe kaplama", "m²", cl(a.ew * 1 - (a.win + a.extd))],
      ["int_facade", "İç cephe kaplama", "m²", cl(((a.iw - a.intd) * 2 + a.ew * 1) - (a.win + a.extd))],
      ...deckRows,
    ];
  }
  return rows.map(([key, label, unit, quantity]) => ({ key, label, unit, quantity }));
}

export function framePriced(k, materialsById, systemType) {
  const comps = frameComponents(k, systemType);
  const sel = Object.fromEntries((k.components || []).map((c) => [c.key, c]));
  let grand = 0;
  const rows = comps.map((c) => {
    const s = sel[c.key] || {};
    const enabled = s.enabled !== false;
    const mat = materialsById[s.material_id];
    const price = mat ? parseFloat(mat.unit_price) || 0 : (parseFloat(s.unit_price) || 0);
    const total = enabled ? c.quantity * price : 0;
    grand += total;
    return { ...c, material_id: s.material_id || null, materialName: mat ? mat.name : "", enabled, unitPrice: price, lineTotal: total };
  });
  const manual = (k.manual_lines || []).map((ml) => {
    const enabled = ml.enabled !== false;
    const mat = materialsById[ml.material_id];
    const price = mat ? parseFloat(mat.unit_price) || 0 : (parseFloat(ml.unit_price) || 0);
    const qty = parseFloat(ml.quantity) || 0;
    const total = enabled ? qty * price : 0;
    grand += total;
    return { id: ml.id, label: ml.label, unit: ml.unit, quantity: qty, material_id: ml.material_id || null, materialName: mat ? mat.name : "", wood_species: ml.wood_species || "", quality: ml.quality || "", enabled, unitPrice: price, lineTotal: total, group: ml.group || "Diğer" };
  });
  return { rows, manual, grand };
}

export const MANUAL_GROUPS = ["Malzeme", "İşçilik", "Nakliye", "Ekstralar", "Diğer"];

// ---------- Structural (yapısal ahşap) ----------
export const DEFAULT_STRUCTURAL = {
  glulam_items: [],
  solid_items: [],
  components: [],
  manual_lines: [],
  overhead_pct: 0,
};

export function structItemMetrics(it) {
  const w = (parseFloat(it.width) || 0) / 100;
  const h = (parseFloat(it.height) || 0) / 100;
  const l = (parseFloat(it.length) || 0) / 100;
  const q = parseFloat(it.qty) || 0;
  return { volume_m3: w * h * l * q, paint_m2: 2 * (w + h) * l * q };
}

export function structTotals(s) {
  const g = { volume: 0, paint: 0 };
  (s.glulam_items || []).forEach((it) => { const m = structItemMetrics(it); g.volume += m.volume_m3; g.paint += m.paint_m2; });
  const sd = { volume: 0, paint: 0 };
  (s.solid_items || []).forEach((it) => { const m = structItemMetrics(it); sd.volume += m.volume_m3; sd.paint += m.paint_m2; });
  return { g, sd };
}

export function structComponents(s) {
  const { g, sd } = structTotals(s);
  const rows = [];
  if ((s.glulam_items || []).length) {
    rows.push(["glulam_volume", "Glulam malzeme hacmi", "m³", g.volume]);
    rows.push(["glulam_paint", "Glulam boya (4 yüzey)", "m²", g.paint]);
    rows.push(["glulam_fire", "Glulam yangın geciktirici boya", "m²", g.paint]);
  }
  if ((s.solid_items || []).length) {
    rows.push(["solid_volume", "Solid malzeme hacmi", "m³", sd.volume]);
    rows.push(["solid_paint", "Solid boya (4 yüzey)", "m²", sd.paint]);
    rows.push(["solid_fire", "Solid yangın geciktirici", "m³", sd.volume]);
  }
  return rows.map(([key, label, unit, quantity]) => ({ key, label, unit, quantity }));
}

export function structPriced(s, materialsById) {
  const comps = structComponents(s);
  const sel = Object.fromEntries((s.components || []).map((c) => [c.key, c]));
  let grand = 0;
  const rows = comps.map((c) => {
    const sc = sel[c.key] || {};
    const enabled = sc.enabled !== false;
    const mat = materialsById[sc.material_id];
    const price = mat ? parseFloat(mat.unit_price) || 0 : (parseFloat(sc.unit_price) || 0);
    const total = enabled ? c.quantity * price : 0;
    grand += total;
    return { ...c, material_id: sc.material_id || null, materialName: mat ? mat.name : "", wood_species: sc.wood_species || "", quality: sc.quality || "", enabled, unitPrice: price, lineTotal: total };
  });
  const manual = (s.manual_lines || []).map((ml) => {
    const enabled = ml.enabled !== false;
    const mat = materialsById[ml.material_id];
    const price = mat ? parseFloat(mat.unit_price) || 0 : (parseFloat(ml.unit_price) || 0);
    const qty = parseFloat(ml.quantity) || 0;
    const total = enabled ? qty * price : 0;
    grand += total;
    return { id: ml.id, label: ml.label, unit: ml.unit, quantity: qty, material_id: ml.material_id || null, materialName: mat ? mat.name : "", wood_species: ml.wood_species || "", quality: ml.quality || "", enabled, unitPrice: price, lineTotal: total, group: ml.group || "Diğer" };
  });
  return { rows, manual, grand };
}
